// @flow

import * as React from 'react';
import { useSystem } from 'react-system';
import { graphql } from 'react-relay';
import { toAsUrl } from '../utils/rewrites';
import { type NextQuery, nextQuery } from '../controls/relay';
import { MuiThemeProvider } from '../controls/mui-providers';
import { Layout } from '../shared/Layout.js';
import { AggregatesListingDialog } from '../shared/AggregatesListings/aggregates-listing-dialog';
import { AggregatesListingPageMeta } from '../shared/AggregatesListings/AggregatesListingPageMeta';
import { makeListingHref } from '../shared/AggregatesListings/listing-href';
import { localeToPlaceLang } from '../utils/localeToLng';
import { listingsDescriptor } from '../shared/AggregatesListings/listingsDescriptor';
import type {
  listingQuery,
  listingQueryVariables,
} from './__generated__/listingQuery.graphql';

const ListingPage = props => {
  const { media } = useSystem();

  const backHref = React.useMemo(() => {
    const activeListing = props.data.listing;

    if (activeListing == null) {
      return {
        pathname: '/listings',
        query: {
          offerType: 'buy',
        },
      };
    }

    const { lang } = props.variables;

    const municipalitySlug =
      activeListing.municipalityObject?.placeForLink.slug;
    const compositePropertyType = activeListing.compositePropertyType;

    const placeSlugs =
      municipalitySlug != null
        ? [
            {
              slug: municipalitySlug,
              lang: localeToPlaceLang(lang),
            },
          ]
        : null;

    const href = {
      pathname: '/listings',
      query: listingsDescriptor.getQueryParams({
        offerType: activeListing.offerType,
        placeSlugs,
        compositePropertyType_eq: compositePropertyType,
      }),
    };
    return href;
  }, [props.data.listing, props.variables]);

  return (
    <MuiThemeProvider>
      <Layout
        root={props.data}
        pageWithData={props.data.cms.pageWithData}
        headerStyle="listings-page"
        // headerContent={<ListingsLayoutHeaderContent root={props.data} />}
        showFooter={false}
        // logoHref={offerType === 'buy' ? buyLogoHref : rentLogoHref}
        styles={{
          // increase margin because of floating "View on" button
          footer: media({ marginBottom: ['32px', 0] }),
        }}
      >
        {props.data.listing ? (
          <AggregatesListingPageMeta
            listing={props.data.listing}
            page={props.data.cms.listingPage?.page}
          />
        ) : null}

        <AggregatesListingDialog
          listing={props.data.listing}
          shallowCardsLink={false}
          replaceCardsLink={false}
          backHref={backHref}
        />
      </Layout>
    </MuiThemeProvider>
  );
};

const safeJsonParse = str => {
  try {
    return JSON.parse(str ?? '');
  } catch (e) {
    return null;
  }
};

const ListingsP: NextQuery<
  listingQuery,
  {|
    lang?: string,
    uid?: string,
    listingSlug?: string,
    compositePropertyType_eq?: string,
    offerType?: string,
  |},
> = nextQuery((ctx, userData) => {
  const queryParsed = {
    compositePropertyType_eq: safeJsonParse(ctx.query.compositePropertyType_eq),
    offerType: safeJsonParse(ctx.query.offerType),
    listingSlug: safeJsonParse(ctx.query.listingSlug),
  };

  const variables: listingQueryVariables = {
    uid: ctx.query.uid ?? '',
    lang: ctx.query.lang ?? '',
    listingSlug: queryParsed.listingSlug ?? '',
  };

  return {
    query: graphql`
      query listingQuery($uid: String!, $lang: String!, $listingSlug: String!) {
        ...Layout_root @arguments(lang: $lang)

        listing: aggregatesListingBySlug(slug: $listingSlug) {
          # used for redirect
          slug
          compositePropertyType
          municipalityObject {
            placeForLink {
              slug
            }
          }
          offerType

          ...AggregatesListingPageMeta_listing
          ...aggregatesListingDialog_listing
        }

        fallbackPlace: placeByListingSlug(slug: $listingSlug) {
          slug
          canonicalPlace {
            slug
          }
        }

        cms {
          pageWithData(uid: $uid, lang: $lang) {
            ...Layout_pageWithData
          }

          listingPage: pageWithData(uid: "listing", lang: $lang) {
            ...Layout_pageWithData
            page {
              ...AggregatesListingPageMeta_page
            }
          }
        }
      }
    `,
    variables,
    cacheStrategy: 'none',
    validate: data => {
      const {
        featureConfig: { disableAggregator },
      } = userData;

      const redirect = hrefObj => ({
        type: 'redirect',
        statusCode: 301,
        location: toAsUrl(userData.routes, userData.language, hrefObj, {
          pathname: '/listing',
          query: ctx.query,
        }).as,
      });

      if (disableAggregator) {
        return { type: 'redirect', statusCode: 307, location: '/' };
      }

      const { listing } = data;

      // redirect to listings result page if detail listing is offline
      if (listing == null) {
        return redirect({
          pathname: '/listings',
          query: listingsDescriptor.getQueryParams({
            offerType: queryParsed.offerType ?? 'buy',
            compositePropertyType_eq: queryParsed.compositePropertyType_eq,
            placeSlugs:
              data.fallbackPlace == null
                ? []
                : [
                    {
                      slug:
                        data.fallbackPlace.canonicalPlace?.slug ??
                        data.fallbackPlace.slug,
                      lang: (ctx.query.lang: any),
                    },
                  ],
          }),
        });
      }

      const { slug, compositePropertyType } = listing;

      // redirect if detail listing slug is wrong
      if (
        slug !== queryParsed.listingSlug ||
        compositePropertyType !== queryParsed.compositePropertyType_eq
      ) {
        return redirect(
          makeListingHref({
            offerType: data.listing?.offerType ?? 'buy',
            slug,
            compositePropertyType,
          }),
        );
      }

      return null;
    },
  };
})(ListingPage);

export default ListingsP;
